import { useState, useEffect } from 'react'
import Layout from '../components/Layout'
import { useWallet } from '../contexts/WalletContext'
import { apiFetch } from '../config/api'
import { formatCHNX } from '../utils/formatCHNX'

interface UserStats {
  userAddress: string
  totalDuels: number
  wonDuels: number
  lostDuels: number
  drawDuels: number
  cancelledDuels: number
  totalWagered: string
  totalWon: string
  totalLost: string
  winRate: string
  profitLoss: string
  rank: number | null
}

interface LeaderboardEntry {
  rank: number
  userAddress: string
  totalWon: string
  totalDuels: number
  wonDuels: number
  winRate: string
  profitLoss: string
}

interface PlatformStats {
  totalDuels: number
  totalWagered: string
  totalUsers: number
  activeDuels: number
}

export default function Profile() {
  const { address: walletAddress, isConnected } = useWallet()
  const [userStats, setUserStats] = useState<UserStats | null>(null)
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'stats' | 'leaderboard' | 'platform'>('stats')

  useEffect(() => {
    if (isConnected && walletAddress) {
      fetchData(walletAddress)
    }
  }, [isConnected, walletAddress])

  const fetchData = async (address: string) => {
    try {
      // Fetch user stats
      const statsResponse = await apiFetch(`/duel/stats/${address}`)
      const statsData = await statsResponse.json()
      if (statsData.success) {
        setUserStats(statsData.data)
      }

      // Fetch leaderboard
      const leaderboardResponse = await apiFetch('/duel/stats/leaderboard?limit=10')
      const leaderboardData = await leaderboardResponse.json()
      if (leaderboardData.success) {
        setLeaderboard(leaderboardData.data.leaderboard || [])
      }

      // Fetch platform stats
      const platformResponse = await apiFetch('/duel/stats/platform')
      const platformData = await platformResponse.json()
      if (platformData.success) {
        setPlatformStats(platformData.data)
      }
    } catch (error) {
      console.error('Failed to fetch profile data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading profile...</div>
      </div>
    )
  }

  return (
    <Layout>
      <div>
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Profile & Statistics</h2>
        {walletAddress && (
          <p className="mt-2 text-gray-600">
            Your wallet: {walletAddress.slice(0, 10)}...{walletAddress.slice(-8)}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('stats')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'stats'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Stats
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'leaderboard'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Leaderboard
          </button>
          <button
            onClick={() => setActiveTab('platform')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'platform'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Platform Stats
          </button>
        </nav>
      </div>

      {/* My Stats Tab */}
      {activeTab === 'stats' && userStats && (
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm text-gray-600 mb-1">Total Duels</p>
              <p className="text-3xl font-bold text-gray-900">{userStats.totalDuels}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm text-gray-600 mb-1">Win Rate</p>
              <p className="text-3xl font-bold text-green-600">{userStats.winRate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm text-gray-600 mb-1">Total Wagered</p>
              <p className="text-3xl font-bold text-blue-600">{formatCHNX(userStats.totalWagered)}</p>
              <p className="text-xs text-gray-500">CHNX</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm text-gray-600 mb-1">Profit/Loss</p>
              <p className={`text-3xl font-bold ${
                parseFloat(userStats.profitLoss) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {parseFloat(userStats.profitLoss) >= 0 ? '+' : ''}
                {formatCHNX(userStats.profitLoss)}
              </p>
              <p className="text-xs text-gray-500">CHNX</p>
            </div>
          </div>

          {/* Detailed Stats */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Detailed Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-2">Won Duels</p>
                <p className="text-2xl font-semibold text-green-600">{userStats.wonDuels}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Lost Duels</p>
                <p className="text-2xl font-semibold text-red-600">{userStats.lostDuels}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Draw Duels</p>
                <p className="text-2xl font-semibold text-gray-600">{userStats.drawDuels}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Cancelled</p>
                <p className="text-2xl font-semibold text-gray-400">{userStats.cancelledDuels}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Total Won</p>
                <p className="text-2xl font-semibold text-green-600">{formatCHNX(userStats.totalWon)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Total Lost</p>
                <p className="text-2xl font-semibold text-red-600">{formatCHNX(userStats.totalLost)}</p>
              </div>
            </div>
          </div>

          {/* Rank */}
          {userStats.rank && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Your Rank</p>
                  <p className="text-4xl font-bold text-orange-600">#{userStats.rank}</p>
                </div>
                <div className="text-6xl">🏆</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600">
            <h3 className="text-2xl font-bold text-white">🏆 Top Players</h3>
            <p className="text-indigo-100 text-sm mt-1">Compete for the top spot!</p>
          </div>
          
          {leaderboard.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">No leaderboard data yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Won</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Win Rate</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Duels</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profit/Loss</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leaderboard.map((entry) => (
                    <tr
                      key={entry.userAddress}
                      className={entry.userAddress.toLowerCase() === (walletAddress || '').toLowerCase() ? 'bg-indigo-50' : ''}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {entry.rank <= 3 && (
                            <span className="text-xl">
                              {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'}
                            </span>
                          )}
                          <span className="font-semibold">{entry.rank}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="font-mono text-sm">
                          {entry.userAddress.slice(0, 10)}...{entry.userAddress.slice(-6)}
                        </p>
                        {entry.userAddress.toLowerCase() === (walletAddress || '').toLowerCase() && (
                          <span className="text-xs text-indigo-600 font-medium">You</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="font-semibold text-green-600">
                          {formatCHNX(entry.totalWon)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="font-semibold">{entry.winRate}%</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-gray-600">{entry.wonDuels}/{entry.totalDuels}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className={`font-semibold ${
                          parseFloat(entry.profitLoss) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {parseFloat(entry.profitLoss) >= 0 ? '+' : ''}
                          {formatCHNX(entry.profitLoss)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Platform Stats Tab */}
      {activeTab === 'platform' && platformStats && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm text-gray-600 mb-1">Total Duels</p>
              <p className="text-3xl font-bold text-indigo-600">{platformStats.totalDuels}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm text-gray-600 mb-1">Active Duels</p>
              <p className="text-3xl font-bold text-blue-600">{platformStats.activeDuels}</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm text-gray-600 mb-1">Total Wagered</p>
              <p className="text-3xl font-bold text-purple-600">
                {parseFloat(platformStats.totalWagered).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">CHNX</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm text-gray-600 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-green-600">{platformStats.totalUsers}</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-md p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">🎉 Join the Community!</h3>
            <p className="text-indigo-100 mb-6">
              {platformStats.totalUsers} players have already joined the Duel DApp. 
              Create your first duel and compete for the top spot on the leaderboard!
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <p className="text-indigo-100 text-sm">Avg per User</p>
                <p className="text-2xl font-bold">
                  {platformStats.totalUsers > 0 
                    ? (platformStats.totalDuels / platformStats.totalUsers).toFixed(1)
                    : '0'}
                </p>
                <p className="text-indigo-100 text-xs">duels</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <p className="text-indigo-100 text-sm">Success Rate</p>
                <p className="text-2xl font-bold">
                  {platformStats.totalDuels > 0 
                    ? ((platformStats.totalDuels - platformStats.activeDuels) / platformStats.totalDuels * 100).toFixed(1)
                    : '0'}%
                </p>
                <p className="text-indigo-100 text-xs">matched</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <p className="text-indigo-100 text-sm">Activity</p>
                <p className="text-2xl font-bold">
                  {platformStats.activeDuels > 0 ? '🔥' : '😴'}
                </p>
                <p className="text-indigo-100 text-xs">
                  {platformStats.activeDuels > 0 ? 'Hot' : 'Quiet'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  )
}
